import { IProduct } from '../../domains/Product/Product'

import { IEnrichV2Product, IEnrichV2Variant, IEnrichV2VariantAttributeValue, IEnrichV2Vda } from './enrich-v2.types'

/** v1 legacy variant title joins axis values with this separator (e.g. "XS / Black"). */
export const V1_TITLE_SEPARATOR = ' / '

/**
 * VDA axes that participate in variant option/title synthesis: visible axes
 * ordered by `priority` asc (v1 legacy title order — size before color).
 */
export const getDisplayableVdas = (vdas: IEnrichV2Vda[] | undefined): IEnrichV2Vda[] => {
  if (!vdas || vdas.length === 0) return []
  return vdas.filter(vda => !vda.isHidden).sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
}

/**
 * Rebuilds the v1 `variant.options` pairs (`{ name, value }`) from the v2
 * axis definitions + this variant's `attributeValues`.
 *
 * Uses the canonical `value` (NOT `localisedValue`) — options feed
 * variant-matching logic which must stay locale-stable. Display layers that
 * want localisation read the passed-through `attributeValues` instead.
 * Axes with no value on this variant are dropped (v1 omitted them too).
 */
export const buildVariantOptionsFromVdas = (
  vdas: IEnrichV2Vda[] | undefined,
  attributeValues: Record<string, IEnrichV2VariantAttributeValue> | undefined,
): { name: string; value: string }[] => {
  return getDisplayableVdas(vdas)
    .map(vda => ({
      name: vda.name,
      value: attributeValues?.[vda.key]?.value ?? '',
    }))
    .filter(option => Boolean(option.name) && Boolean(option.value))
}

/**
 * Rebuilds the v1 `variant.title` (`"XS / Black"`) from the synthesized
 * options — axis values joined by `" / "` in priority order.
 */
export const buildVariantTitleFromOptions = (options: { name: string; value: string }[]): string => {
  return options.map(option => option.value).join(V1_TITLE_SEPARATOR)
}

/**
 * Minimal v1 `productOptions` synthesis so the `Product` domain object never
 * carries `undefined` where v1 had an array. Cart does not consume
 * `productOptions`; PDP-grade fidelity (ProductOptionName enums, display
 * types) is intentionally out of scope for the cart cutover.
 */
export const buildProductOptionsFromVdas = (
  vdas: IEnrichV2Vda[] | undefined,
): {
  name: string
  displayName: string
  displaySequence: number
  values: { value: string; displayName?: string; hexCode?: string }[]
}[] => {
  return getDisplayableVdas(vdas).map((vda, index) => ({
    name: vda.name,
    displayName: vda.name,
    displaySequence: vda.priority ?? index + 1,
    values: (vda.values ?? []).map(value => ({
      value: value.value,
      displayName: value.localisedValue,
      hexCode: value.hexCode,
    })),
  }))
}

const adaptVariant = (variant: IEnrichV2Variant, vdas: IEnrichV2Vda[] | undefined) => {
  const options = buildVariantOptionsFromVdas(vdas, variant.attributeValues)
  return {
    ...variant,
    // v1 field names consumed across cart line items + analytics.
    // The contract doc renamed these to shopifyVariantId/shopifyProductId but
    // the deployed v2 payload (verified against pre-integ) still sends the v1
    // names svid/spid — read whichever is present, never overwrite a good
    // spread value with undefined.
    svid: variant.svid ?? variant.shopifyVariantId,
    spid: variant.spid ?? variant.shopifyProductId,
    options,
    title: buildVariantTitleFromOptions(options),
  }
}

/**
 * Adapts one `GET /products/v2/enrich` product to the v1 `IProduct` shape the
 * cart merge helpers + `Product` domain consume, so downstream code stays
 * byte-compatible whether the payload came from v1 or v2.
 *
 * v2-native data (`vdas`, per-variant `attributeValues`, `anchorAttributes`)
 * is passed through additively for Smart Variant show-bucket renderers.
 */
export const adaptEnrichV2Product = (product: IEnrichV2Product): IProduct => {
  // Deployed v2 payload sends product-level `spid` (string); contract doc says
  // `shopifyProductId` — accept both, preferring whichever is present.
  const { vdas, variants, spid, shopifyProductId, ...rest } = product

  return {
    ...rest,
    spid: spid ?? shopifyProductId,
    shopifyProductId: shopifyProductId ?? spid,
    productOptions: buildProductOptionsFromVdas(vdas),
    vdas,
    variants: (variants ?? []).map(variant => adaptVariant(variant, vdas)),
  } as unknown as IProduct
}
